/**
 * Terminal `INVALIDATE` step. Mid-pipeline mutations pass `skipInvalidate:true`
 * to keep RTKQ from refetching against partial state; this step fires one
 * dispatch at the end. Failed pipelines skip it (executor enforces) so the
 * cache stays on pre-mutation state behind the error toast. Tag set comes
 * from the intent kind via `getInvalidationScope`.
 */

import {
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_ROUTE_STOP_LISTS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_ROUTES_METADATA_BY_DATE,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
} from 'api/cacheTagTypes'
import { UNASSIGNED_ROUTE_ID } from 'containers/new-route-manager/board-view-v2/settings'
import { ok } from 'types/entities/shared/result'

import type { StepResult } from '../types'
import type { Deps, PipelineStep, RouteOpIntent, RouteOpTag } from 'types/modules/routing'

/**
 * Tag scope for daily mutations. Includes the unassigned-work-orders list and
 * per-date route metadata so an assign/unassign/move refreshes the Unassigned
 * column and the day's stop counts (the board-view-2 DnD path depends on this).
 */
export const DAILY_INVALIDATION_TAGS = [
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS,
  CACHE_TAG_ROUTES_METADATA_BY_DATE,
] as const

/** Tag scope for master mutations — mirrors `LIVE_PUBLISH_INVALIDATION_TAGS` in `masterRoute.ts`. */
export const MASTER_INVALIDATION_TAGS = [
  CACHE_TAG_MASTER_ROUTE_META,
  CACHE_TAG_MASTER_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES,
  CACHE_TAG_ROUTES_STOPS,
  CACHE_TAG_ROUTES_GEOJSON,
] as const

/** The one route's lane and aggregates, as `getDailyLane` and `getLaneAggregates` provide them. */
export const routeStopsTag = (routeId: string): RouteOpTag => ({ type: CACHE_TAG_ROUTES_STOPS, id: routeId })

/** What a move within the day leaves stale beyond the routes it touched: other stop lists, the day's shape and history. */
export const SCOPED_DAILY_INVALIDATION_TAGS = [
  CACHE_TAG_ROUTE_STOP_LISTS,
  CACHE_TAG_ROUTES_GEOJSON,
  CACHE_TAG_DAILY_ROUTE_VERSIONS,
  CACHE_TAG_ROUTES_METADATA_BY_DATE,
] as const

export const SCOPED_MASTER_INVALIDATION_TAGS = [CACHE_TAG_MASTER_ROUTE_META, CACHE_TAG_MASTER_ROUTE_VERSIONS] as const

/**
 * The tags an intent invalidates, by kind; `includeMaster` widens a daily op to the master tags. A same-day move
 * names its routes, so only their lanes refetch.
 */
export const getInvalidationScope = (intent: RouteOpIntent): ReadonlyArray<RouteOpTag> => {
  switch (intent.kind) {
    case 'change-day-of-week':
    case 'restore-master-version':
    case 'sync-daily-to-master':
      return [...MASTER_INVALIDATION_TAGS]
    case 'complete':
    case 'change-date':
    case 'restore-daily-version':
      return [...DAILY_INVALIDATION_TAGS]
    case 'assign':
    case 'unassign':
    case 'resequence':
    case 'move': {
      // A card dragged out of the Unassigned column arrives as a move from the sentinel, whose lane has its own tag.
      const fromUnassigned = intent.kind === 'move' && intent.sourceRouteId === UNASSIGNED_ROUTE_ID
      const routeIds = intent.kind === 'move' ? [intent.sourceRouteId, intent.routeId] : [intent.routeId]
      const touchesUnassigned = intent.kind === 'assign' || intent.kind === 'unassign' || fromUnassigned
      return [
        ...routeIds.filter(routeId => routeId !== UNASSIGNED_ROUTE_ID).map(routeStopsTag),
        ...(touchesUnassigned ? [CACHE_TAG_ROUTES_UNASSIGNED_WORK_ORDERS] : []),
        ...SCOPED_DAILY_INVALIDATION_TAGS,
        ...(intent.includeMaster ? SCOPED_MASTER_INVALIDATION_TAGS : []),
      ]
    }
    case 'master-publish':
      return Array.from(new Set<RouteOpTag>([...DAILY_INVALIDATION_TAGS, ...MASTER_INVALIDATION_TAGS]))
    default: {
      // Exhaustiveness check — if a new RouteOpIntent kind is added without
      // a case above, tsc rejects this assignment at compile time.
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const exhaustiveCheck: never = intent
      return [...DAILY_INVALIDATION_TAGS]
    }
  }
}

export interface InvalidateStepArgs {
  readonly intent: RouteOpIntent
  /** Optional override — defaults to `getInvalidationScope(intent)`. */
  readonly tags?: ReadonlyArray<RouteOpTag>
}

export type InvalidateStepResult = StepResult<'INVALIDATE', { readonly tags: ReadonlyArray<RouteOpTag> }>

const STEP_ID = 'invalidate'

export const makeInvalidateStep = (args: InvalidateStepArgs, deps: Deps): PipelineStep<'INVALIDATE', InvalidateStepResult> => {
  const tags = args.tags ?? getInvalidationScope(args.intent)
  return {
    id: STEP_ID,
    phase: 'INVALIDATE',
    label: 'invalidate caches',
    applyOrder: Number.MAX_SAFE_INTEGER,
    run: async () => {
      deps.dispatch(deps.api.util.invalidateTags(tags))
      return ok({ phase: 'INVALIDATE', stepId: STEP_ID, value: { tags } })
    },
  }
}
